import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Package, Plus, ShoppingCart, Truck, FileText, Boxes } from 'lucide-react';
import { CatalogBanner } from '@/components/layout/CatalogBanner';
import { ProductImage } from '@/components/catalog/ProductImage';
import { PrecioDisplay } from '@/components/ui/PrecioDisplay';
import { cn } from '@/lib/utils';

export interface DetailSpec {
  label: string;
  value: string;
  href?: string;
}

export interface DetailBreadcrumb {
  label: string;
  to?: string;
}

interface ItemDetailLayoutProps {
  breadcrumbs: DetailBreadcrumb[];
  badge?: string;
  title: string;
  subtitle?: string;
  imagen?: string | null;
  imagenAlt: string;
  imagenCategoria?: string | null;
  marca?: string | null;
  precio: number;
  precioLista?: number | null;
  existencias: number;
  existenciaTexto?: string;
  descripcion: string;
  specs: DetailSpec[];
  children?: ReactNode;
  maxCantidad?: number;
  cantidadInicial?: number;
  onAgregar: (cantidad: number) => void;
  tipo?: 'producto' | 'kit';
}

function TrustNotes() {
  const items = [
    { icon: Boxes, text: 'Existencia mostrada en tiempo real' },
    { icon: Truck, text: 'Envío nacional por paquetería' },
    { icon: FileText, text: 'Factura disponible en el checkout' },
  ];
  return (
    <ul className="mt-6 space-y-2 border-t border-surface-100 pt-5">
      {items.map(({ icon: Icon, text }) => (
        <li key={text} className="flex items-center gap-2 text-xs text-surface-600">
          <Icon className="h-3.5 w-3.5 shrink-0 text-brand-600" />
          {text}
        </li>
      ))}
    </ul>
  );
}

export function ItemDetailLayout({
  breadcrumbs,
  badge,
  title,
  subtitle,
  imagen,
  imagenAlt,
  imagenCategoria,
  marca,
  precio,
  precioLista,
  existencias,
  existenciaTexto,
  descripcion,
  specs,
  children,
  maxCantidad = 99,
  cantidadInicial = 1,
  onAgregar,
  tipo = 'producto',
}: ItemDetailLayoutProps) {
  const [qty, setQty] = useState(cantidadInicial);
  const sinStock = existencias <= 0;
  const maxQty = sinStock ? 1 : Math.min(maxCantidad, existencias);

  const stockLabel =
    existenciaTexto ??
    (sinStock
      ? 'Agotado — consulta disponibilidad con ventas'
      : existencias <= 5
        ? `Últimas ${existencias} piezas disponibles`
        : `${existencias} disponibles en almacén`);

  return (
    <>
      <CatalogBanner
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumbs}
      />

      <div className="page-section pb-28 lg:pb-12">
        <div className="container-app">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 xl:gap-16">
            {/* Imagen */}
            <div className="lg:sticky lg:top-20 lg:self-start">
              <div className="overflow-hidden rounded-xl border border-surface-200 bg-white shadow-sm">
                <div className="relative aspect-square max-h-[70vh] sm:max-h-[520px]">
                  <ProductImage
                    imagen={imagen}
                    alt={imagenAlt}
                    fallbackLabel={imagenAlt}
                    categoria={imagenCategoria ?? (tipo === 'kit' ? 'kit' : undefined)}
                    className="h-full w-full"
                    priority
                    fit="contain"
                  />
                  {badge && (
                    <span className="absolute left-3 top-3 rounded-md bg-white/95 px-2.5 py-1 text-xs font-semibold text-surface-800 shadow-sm">
                      {badge}
                    </span>
                  )}
                  {precioLista && precioLista > precio && (
                    <span className="badge-offer absolute right-3 top-3">Oferta</span>
                  )}
                </div>
              </div>
            </div>

            {/* Compra + resumen */}
            <div>
              {marca && (
                <p className="text-sm font-medium uppercase tracking-wide text-surface-500">{marca}</p>
              )}
              <h1 className="mt-1 font-display text-2xl font-bold leading-tight text-surface-950 sm:text-3xl">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-2 text-sm text-surface-600">{subtitle}</p>
              )}

              <div className="mt-6 rounded-xl border border-surface-200 bg-surface-50/80 p-5">
                <PrecioDisplay
                  precio={precio}
                  precioLista={precioLista}
                  cantidad={qty}
                  variant="detail"
                  etiquetaPrecio={tipo === 'kit' ? 'precio total del kit, sin IVA' : 'precio unitario, sin IVA'}
                />

                <p
                  className={cn(
                    'mt-3 text-sm font-medium',
                    sinStock ? 'text-red-600' : existencias <= 5 ? 'text-amber-700' : 'text-emerald-700',
                  )}
                >
                  {stockLabel}
                </p>

                <div className="mt-5 hidden gap-3 sm:flex sm:items-center lg:flex">
                  <div className="inline-flex items-center rounded-lg border border-surface-300 bg-white">
                    <button
                      type="button"
                      onClick={() => setQty(Math.max(1, qty - 1))}
                      className="flex h-11 w-11 items-center justify-center hover:bg-surface-50"
                      aria-label="Menos"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="min-w-[2.75rem] text-center text-sm font-semibold tabular-nums">{qty}</span>
                    <button
                      type="button"
                      onClick={() => setQty(Math.min(maxQty, qty + 1))}
                      disabled={qty >= maxQty}
                      className="flex h-11 w-11 items-center justify-center hover:bg-surface-50 disabled:opacity-40"
                      aria-label="Más"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    disabled={sinStock}
                    onClick={() => onAgregar(qty)}
                    className="btn-primary min-h-[44px] flex-1"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Agregar al carrito
                  </button>
                </div>

                <TrustNotes />
              </div>

              {descripcion && (
                <div className="mt-8">
                  <h2 className="text-sm font-semibold text-surface-900">Descripción</h2>
                  <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-surface-700">
                    {descripcion}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Ficha técnica + contenido extra (kit: incluye) */}
          <div className="mt-12 grid gap-8 lg:grid-cols-5">
            {specs.length > 0 && (
              <div className="lg:col-span-2">
                <h2 className="text-sm font-semibold text-surface-900">Ficha del producto</h2>
                <dl className="mt-3 divide-y divide-surface-100 rounded-xl border border-surface-200 bg-white">
                  {specs.map((s) => (
                    <div key={s.label} className="grid grid-cols-[9rem_1fr] gap-3 px-4 py-3 text-sm">
                      <dt className="text-surface-500">{s.label}</dt>
                      <dd className="font-medium text-surface-900">
                        {s.href ? (
                          <Link to={s.href} className="text-brand-700 hover:underline">
                            {s.value}
                          </Link>
                        ) : (
                          s.value
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {children && <div className={cn('lg:col-span-3', specs.length === 0 && 'lg:col-span-5')}>{children}</div>}
          </div>
        </div>
      </div>

      {/* Barra móvil */}
      <div className="mobile-action-bar lg:hidden">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-surface-500">{tipo === 'kit' ? 'Kit' : subtitle}</p>
          <PrecioDisplay precio={precio} cantidad={qty} variant="inline" className="text-lg font-bold" />
        </div>
        <div className="inline-flex shrink-0 items-center rounded-lg border border-surface-300 bg-white">
          <button
            type="button"
            onClick={() => setQty(Math.max(1, qty - 1))}
            className="flex h-10 w-9 items-center justify-center"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="min-w-[1.75rem] text-center text-sm font-semibold tabular-nums">{qty}</span>
          <button
            type="button"
            onClick={() => setQty(Math.min(maxQty, qty + 1))}
            className="flex h-10 w-9 items-center justify-center"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          disabled={sinStock}
          onClick={() => onAgregar(qty)}
          className="btn-primary shrink-0 px-4 py-2.5"
        >
          <ShoppingCart className="h-4 w-4" />
          Agregar
        </button>
      </div>
    </>
  );
}

export function KitIncludesSection({
  productos,
}: {
  productos: { id_pto: number; nombre_pto: string; modelo_pto: string; cantidad: number }[];
}) {
  const totalPiezas = productos.reduce((n, p) => n + p.cantidad, 0);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-sm font-semibold text-surface-900">Qué incluye este kit</h2>
        <span className="text-xs text-surface-500">{totalPiezas} piezas en total</span>
      </div>
      <ul className="mt-3 divide-y divide-surface-100 overflow-hidden rounded-xl border border-surface-200 bg-white">
        {productos.map((p) => (
          <li key={p.id_pto}>
            <Link
              to={`/producto/${p.id_pto}`}
              className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-surface-50"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                <Package className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-surface-900">
                  <span className="text-brand-700">{p.cantidad}×</span> {p.nombre_pto}
                </p>
                <p className="text-xs text-surface-500">Modelo {p.modelo_pto}</p>
              </div>
              <span className="shrink-0 text-xs font-medium text-brand-700">Ver producto →</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
